// src/utils/pushSubscription.js
//
// Browser-side helpers for Web Push, shared by the opt-in hook and by logout.

// Re-exported so existing callers keep one import for the push-related browser checks.
export { isIos, isStandalone } from './platform';

/** Is Web Push usable here at all (secure context, service worker, PushManager)? */
export function pushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

/** The registered service worker, or null — in `npm start` none is registered. */
export async function getRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration()) || null;
}

/** Web Push wants the VAPID key as bytes; the server hands it over as base64url. */
export function urlBase64ToUint8Array(base64) {
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = window.atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/**
 * Stop pushes reaching this browser for the current person. Called on logout and
 * on session expiry so a shared phone never shows the previous person's
 * notifications to the next one.
 *
 * `notifyServer` needs a still-valid token (logout); on expiry only the local
 * unsubscribe is possible, and the server prunes the row on its next 410.
 * The token is captured synchronously, before the caller clears it.
 */
export async function dropPushSubscription({ notifyServer }) {
  try {
    const token = localStorage.getItem('previu_token');
    const reg = await getRegistration();
    const sub = reg && reg.pushManager ? await reg.pushManager.getSubscription() : null;
    if (!sub) return;
    if (notifyServer && token) {
      // Loaded on demand: services/api imports sessionExpiry, which imports this file.
      const { pushAPI } = await import('../services/pushAPI');
      await pushAPI.unsubscribe(sub.endpoint, token).catch(() => {});
    }
    await sub.unsubscribe();
  } catch (e) {
    // Best effort — never block a logout on this.
  }
}
